import type { UUID } from './ids';

/** أنواع الاشتراكات التجارية. لا تربط حالة الاشتراك بحالة المنشأة. */
export type BillingInterval = 'monthly' | 'yearly' | 'custom';

export interface SubscriptionPlan {
  id: UUID;
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  billingInterval: BillingInterval;
  trialDays: number;
  features: Record<string, unknown>;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * حالات الاشتراك منفصلة تمامًا عن حالة المنشأة وعن حالة الدفع:
 * - المنشأة قد تكون نشطة والاشتراك متأخرًا أو ملغيًا.
 * - الدفع قد يكون مكتملًا دون أن يغيّر حالة الاشتراك تلقائيًا.
 */
export type SubscriptionStatus =
  | 'pending'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'expired';

export type SubscriptionSource = 'manual' | 'self_service';

export interface CompanySubscription {
  id: UUID;
  companyId: UUID;
  planId: UUID | null;
  status: SubscriptionStatus;
  startsAt: string | null;
  endsAt: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  source: SubscriptionSource;
  notes: string | null;
  createdBy: UUID | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionEvent {
  id: UUID;
  subscriptionId: UUID;
  companyId: UUID;
  action: string;
  fromStatus: SubscriptionStatus | null;
  toStatus: SubscriptionStatus | null;
  metadata: Record<string, unknown>;
  actorUserId: UUID | null;
  createdAt: string;
}

export type PaymentStatus = 'pending' | 'processing' | 'paid' | 'failed' | 'refunded' | 'void';

/** سجل محاولة/التزام دفع. وجوده لا يفعّل المنشأة ولا يغيّر الاشتراك. */
export interface SubscriptionPayment {
  id: UUID;
  companyId: UUID;
  subscriptionId: UUID | null;
  status: PaymentStatus;
  amountCents: number;
  currency: string;
  dueAt: string | null;
  paidAt: string | null;
  provider: string | null;
  providerRef: string | null;
  clientRef: string | null;
  notes: string | null;
  createdBy: UUID | null;
  createdAt: string;
  updatedAt: string;
}

export type CompanyInviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

/** دعوة آمنة: يُخزَّن تجزئة الرمز فقط، ولا تُخزَّن كلمة مرور أو رمز صريح. */
export interface CompanyInvite {
  id: UUID;
  companyId: UUID;
  email: string;
  roleId: UUID;
  tokenHash: string;
  status: CompanyInviteStatus;
  expiresAt: string;
  createdBy: UUID;
  acceptedBy: UUID | null;
  acceptedAt: string | null;
  createdAt: string;
}

export type RegistrationRequestStatus = 'processing' | 'completed' | 'failed';

/** مفتاح منع تكرار طلب التسجيل الذاتي. */
export interface RegistrationRequest {
  id: UUID;
  userId: UUID;
  requestId: string;
  companyId: UUID | null;
  status: RegistrationRequestStatus;
  createdAt: string;
  updatedAt: string;
}

/** إعدادات تجارية عامة على مستوى المنصة، لا يراها أو يعدّلها إلا مالك المنصة. */
export interface PlatformOperationsSettings {
  id: string;
  selfRegistrationEnabled: boolean;
  subscriptionsEnabled: boolean;
  paymentsEnabled: boolean;
  defaultTrialDays: number;
  defaultPlanId: UUID | null;
  updatedAt: string | null;
  updatedBy: UUID | null;
}

export const DEFAULT_PLATFORM_OPERATIONS_SETTINGS: PlatformOperationsSettings = {
  id: 'platform',
  selfRegistrationEnabled: false,
  subscriptionsEnabled: false,
  paymentsEnabled: false,
  defaultTrialDays: 14,
  defaultPlanId: null,
  updatedAt: null,
  updatedBy: null,
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  pending: 'بانتظار التفعيل',
  trialing: 'تجربة',
  active: 'نشط',
  past_due: 'متأخر',
  canceled: 'ملغي',
  expired: 'منتهي',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'معلّق',
  processing: 'قيد المعالجة',
  paid: 'مدفوع',
  failed: 'فشل',
  refunded: 'مسترد',
  void: 'ملغى',
};

export const BILLING_INTERVAL_LABELS: Record<BillingInterval, string> = {
  monthly: 'شهري',
  yearly: 'سنوي',
  custom: 'مخصص',
};

export const COMPANY_INVITE_STATUS_LABELS: Record<CompanyInviteStatus, string> = {
  pending: 'بانتظار القبول',
  accepted: 'مقبولة',
  revoked: 'ملغاة',
  expired: 'منتهية',
};

export function isSubscriptionStatus(value: string): value is SubscriptionStatus {
  return value in SUBSCRIPTION_STATUS_LABELS;
}

export function isPaymentStatus(value: string): value is PaymentStatus {
  return value in PAYMENT_STATUS_LABELS;
}
